import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Typography,
} from '@mui/material';
import { createContext, ReactNode, useContext, useMemo, JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface DialogContextType {
  createDialog: (props: CustomDialogProps) => void;
  closeDialog: () => void;
  createSimpleConfirm: (props: SimpleConfirmProps) => void;
}

interface CustomDialogProps extends DialogProps {
  children: ReactNode;
  onClose?: (event: object, reason: 'backdropClick' | 'escapeKeyDown') => void;
}

interface SimpleConfirmProps {
  title: ReactNode | string;
  message: ReactNode | string;
  onConfirm: () => void;
  onAbort?: () => void;
  confirmTxt?: string;
  cancelTxt?: string;
}

const DialogContext = createContext<DialogContextType>({} as DialogContextType);

export function DialogProvider({ children }: { children: ReactNode }): JSX.Element {
  const [dialogs, setDialogs] = useState<CustomDialogProps[]>([]);

  const { t } = useTranslation();

  const closeDialog = () => {
    setDialogs((dialogs) => {
      let opens = dialogs.filter((d) => d.open);
      if (opens.length > 0) {
        const latestDialog = opens.pop();
        if (!latestDialog) return dialogs;
        if (latestDialog.onClose) latestDialog.onClose({}, 'backdropClick');
        let idx = dialogs.indexOf(latestDialog);
        dialogs.splice(idx, 1);
        return [...dialogs];
      } else {
        return dialogs;
      }
    });
  };

  const createDialog = (option: CustomDialogProps) => {
    const dialog: CustomDialogProps = {
      ...option,
      onClose: (event, reason) => {
        if (reason === 'backdropClick') {
          closeDialog();
        }
        option.onClose?.(event, reason);
      },
      open: true,
    };
    const updatedDialogs = [];
    dialogs.forEach((d) => updatedDialogs.push(Object.assign({}, d)));
    updatedDialogs.push(dialog);
    setDialogs(updatedDialogs);
  };

  /**
   * Crea un dialog basico da utilizzare in situazioni standard
   * @param title
   * @param message
   * @param onConfirm
   * @param onAbort
   * @param confirmTxt
   * @param cancelTxt
   */
  const createSimpleConfirm = ({
    title,
    message,
    onConfirm,
    onAbort,
    confirmTxt = t('actions.confirm'),
    cancelTxt = t('actions.cancel'),
  }: SimpleConfirmProps) => {
    const abort = () => {
      if (onAbort) {
        onAbort();
      }
      closeDialog();
    };

    const confirm = () => {
      onConfirm();
      closeDialog();
    };

    createDialog({
      open: true,
      maxWidth: 'sm',
      PaperProps: { sx: { position: 'fixed', top: 50, m: 0 } },
      children: (
        <>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent>
            <Typography>{message}</Typography>
          </DialogContent>
          <DialogActions>
            <Button color="primary" variant="outlined" onClick={abort} autoFocus>
              {cancelTxt}
            </Button>
            <Button variant="contained" onClick={confirm}>
              {confirmTxt}
            </Button>
          </DialogActions>
        </>
      ),
    });
  };

  const cachedValue = useMemo(
    () => ({
      createDialog,
      closeDialog,
      createSimpleConfirm,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dialogs],
  );
  return (
    <DialogContext.Provider value={cachedValue}>
      {children}
      {dialogs.map((dialog, i) => {
        return <DialogContainer key={i} {...dialog} />;
      })}
    </DialogContext.Provider>
  );
}

function DialogContainer({ children, onClose, ...rest }: CustomDialogProps) {
  const handleClose = (event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick') {
      // chiusura quando si clicca fuori
      onClose?.(event, reason);
    }
  };

  return (
    <Dialog onClose={handleClose} {...rest}>
      {children}
    </Dialog>
  );
}

export const useDialog = () => useContext(DialogContext);
